/**
 * Landing — the public marketing home page.
 *
 * Eleven sections per the product-copy spec §4 (Hero, Problem, Solution,
 * Parents, Staff, Children, Admin, Privacy, Smart Building, Pricing teaser,
 * Demo CTA). All copy is canonical — match this file as the source of truth
 * for the public site.
 */
import { Link, useNavigate } from "react-router-dom";
import { useState } from "react";
import { toast } from "sonner";
import {
  ArrowRight,
  Sparkles,
  Users,
  MessageCircle,
  CalendarCheck,
  HandHeart,
  ShieldCheck,
  Hand,
  Coffee,
  Moon,
  TreePalm,
  Check,
  Play,
} from "lucide-react";
import MarketingNav from "../components/MarketingNav";
import MarketingFooter from "../components/MarketingFooter";
import { api, setToken } from "../lib/api";
import { useAuth } from "../lib/auth";
import MetaTags from "../components/MetaTags";
import { useI18n } from "../lib/i18n";

export default function Landing() {
  const { t } = useI18n();
  return (
    <div className="relative min-h-screen overflow-x-hidden bg-white">
      <MetaTags
        title={t("landing.meta.title")}
        description={t("landing.meta.description")}
      />
      <MarketingNav />

      <Hero />
      <Problem />
      <Solution />
      <ParentsSection />
      <StaffSection />
      <ChildrenSection />
      <AdminSection />
      <PrivacySection />
      <SmartBuildingSection />
      <PricingTeaser />
      <DemoBand />

      <MarketingFooter />
    </div>
  );
}

/* ---------------- 4.1 Hero ---------------- */
function Hero() {
  const { t } = useI18n();
  const nav = useNavigate();
  const { setUser } = useAuth();
  const [launching, setLaunching] = useState(false);

  async function launchDemo() {
    setLaunching(true);
    try {
      const r = await api.post("/demo/login-as/parent");
      setToken(r.data.token);
      const me = await api.get("/auth/me");
      setUser(me.data);
      toast.success("Welcome to the live demo. Take a calm look around.");
      nav("/home");
    } catch (e) {
      toast.error("Demo could not be started. Please try again.");
    } finally {
      setLaunching(false);
    }
  }
  return (
    <section
      data-testid="hero"
      className="relative pt-32 sm:pt-40 pb-24 sm:pb-32 overflow-hidden"
    >
      {/* Soft purple wash */}
      <div
        aria-hidden
        className="absolute inset-0 -z-10"
        style={{
          background:
            "radial-gradient(60% 50% at 80% 10%, rgba(232, 228, 254, 0.7) 0%, rgba(255,255,255,0) 60%), radial-gradient(40% 40% at 10% 80%, rgba(252, 228, 236, 0.4) 0%, rgba(255,255,255,0) 60%)",
        }}
      />
      <div className="mx-auto max-w-7xl px-6 sm:px-10 grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-7 fade-up">
          <span className="eyebrow" data-testid="hero-eyebrow">
            {t("landing.hero.eyebrow")}
          </span>
          <h1
            data-testid="hero-headline"
            className="mt-6 font-display text-5xl sm:text-6xl lg:text-7xl leading-[1.02] tracking-tight text-[#1A1B2E]"
          >
            {t("landing.hero.headline_lead")}{" "}
            <span className="italic font-light text-[#1F3A2A]">
              {t("landing.hero.headline_italic")}
            </span>
          </h1>
          <p
            data-testid="hero-subheadline"
            className="mt-8 max-w-2xl text-lg sm:text-xl text-[#6B7280] leading-relaxed"
          >
            {t("landing.hero.subheadline")}
          </p>
          <div className="mt-10 flex flex-wrap items-center gap-4">
            <button
              type="button"
              onClick={launchDemo}
              disabled={launching}
              data-testid="hero-cta-live-demo"
              className="btn-primary"
            >
              <Play size={14} strokeWidth={2} />
              {launching ? "Opening the demo…" : "Try the live demo"}
            </button>
            <Link
              to="/demo"
              data-testid="hero-cta-primary"
              className="btn-secondary"
            >
              {t("landing.cta.book_demo")}
              <ArrowRight size={16} strokeWidth={2} />
            </Link>
            <a
              href="#solution"
              data-testid="hero-cta-secondary"
              className="text-sm font-semibold text-[#1F3A2A] hover:text-[#132C17] underline underline-offset-4 transition-colors"
            >
              {t("landing.cta.view_product")}
            </a>
          </div>
          <div
            data-testid="hero-trust"
            className="mt-10 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-[#6B7280]"
          >
            <span className="trust-pill">{t("landing.hero.trust_pill_centers")}</span>
            <span className="trust-pill">{t("landing.hero.trust_pill_compliance")}</span>
            <span className="trust-pill">{t("landing.hero.trust_pill_languages")}</span>
          </div>
        </div>

        {/* Composite mock card */}
        <div className="lg:col-span-5">
          <div className="relative mx-auto max-w-md fade-up">
            <div className="rounded-3xl bg-white border border-[#EEF0F4] shadow-[0_16px_40px_rgba(16,24,40,0.12)] p-6">
              <div className="flex items-center gap-3">
                <div className="h-12 w-12 rounded-full bg-[#DFF5E1] flex items-center justify-center font-display text-xl text-[#1F8A3F]">
                  L
                </div>
                <div>
                  <div className="font-display text-lg text-[#1A1B2E] leading-tight">
                    Leo
                  </div>
                  <div className="text-xs text-[#6B7280]">Sunshine Room</div>
                </div>
                <span className="ml-auto text-[10px] tracking-[0.04em] uppercase text-[#1F8A3F] font-semibold inline-flex items-center gap-1.5">
                  <span aria-hidden className="h-1.5 w-1.5 rounded-full bg-[#34C759]" />
                  Up to date
                </span>
              </div>
              <div className="mt-5 rounded-2xl bg-[#DFF5E1] px-5 py-6 relative overflow-hidden">
                <div className="eyebrow text-[#1A1B2E]/55">Right now</div>
                <div className="mt-2 font-display text-3xl text-[#1A1B2E] leading-tight">
                  In the yard
                </div>
                <p className="mt-2 text-sm text-[#1A1B2E]/70">
                  Updated 2 min ago by Sarah Chen.
                </p>
                <TreePalm
                  size={64}
                  strokeWidth={1.2}
                  className="absolute -bottom-2 -right-1 text-[#1A1B2E]/15"
                />
              </div>
              <div className="mt-5 grid grid-cols-3 gap-2">
                {[
                  { L: "9:00", T: "Morning circle" },
                  { L: "10:00", T: "Outdoor play" },
                  { L: "12:00", T: "Lunch" },
                ].map((p) => (
                  <div
                    key={p.L}
                    className="rounded-xl bg-[#F7F8FC] px-3 py-2.5"
                  >
                    <div className="text-[10px] text-[#9CA3AF] font-semibold">
                      {p.L}
                    </div>
                    <div className="text-[11px] text-[#1A1B2E] font-medium">
                      {p.T}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ---------------- 4.2 Problem ---------------- */
function Problem() {
  return (
    <section
      id="problem"
      data-testid="problem"
      className="border-t border-[#EEF0F4] py-24 sm:py-32 bg-[#F7F8FC]"
    >
      <div className="mx-auto max-w-7xl px-6 sm:px-10">
        <div className="max-w-3xl">
          <span className="eyebrow">The problem</span>
          <h2 className="mt-4 font-display text-4xl sm:text-5xl tracking-tight leading-[1.05] text-[#1A1B2E]">
            The parent group chat
            <br />
            <span className="italic font-light text-[#6B7280]">
              isn't a childcare platform.
            </span>
          </h2>
          <p className="mt-6 text-lg text-[#6B7280] leading-relaxed">
            Centers run on a patchwork — text threads, paper sign-ins, three
            apps that don't talk to each other, and a director's inbox doing the
            work of an operations team. Information slips. Parents worry. Staff
            stay late. Something better is possible.
          </p>
        </div>
        <div className="mt-14 grid md:grid-cols-3 gap-5">
          {[
            {
              t: "Parents feel disconnected.",
              b: "Updates arrive at random. Pickup is uncertain. The day their child just lived is a black box until bedtime.",
              tid: "problem-parents",
            },
            {
              t: "Staff drown in admin.",
              b: "Sign-in sheets, status messages, headcount checks, photo uploads, parent replies — every minute on paperwork is a minute away from the children.",
              tid: "problem-staff",
            },
            {
              t: "Administrators fly blind.",
              b: "Attendance lives in one tool, messages in another, compliance records in a third. Reporting is rebuilt by hand, every time.",
              tid: "problem-admin",
            },
          ].map((c) => (
            <article
              key={c.tid}
              data-testid={c.tid}
              className="rounded-3xl bg-white border border-[#EEF0F4] p-7 shadow-[0_2px_8px_rgba(16,24,40,0.04)]"
            >
              <h3 className="font-display text-2xl text-[#1A1B2E] tracking-tight leading-tight">
                {c.t}
              </h3>
              <p className="mt-3 text-[#6B7280] leading-relaxed text-sm">{c.b}</p>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------------- 4.3 Solution ---------------- */
function Solution() {
  const tiles = [
    {
      title: "For parents",
      promise: "Know where they are. Know they're okay.",
      href: "#parents",
      Icon: HandHeart,
      bg: "var(--pastel-mint)",
      fg: "#1F8A3F",
      tid: "solution-parents",
    },
    {
      title: "For staff",
      promise: "Less paperwork. More care.",
      href: "#staff",
      Icon: Hand,
      bg: "var(--pastel-lavender)",
      fg: "#1F3A2A",
      tid: "solution-staff",
    },
    {
      title: "For children",
      promise: "Made for small hands.",
      href: "#children",
      Icon: Sparkles,
      bg: "var(--pastel-yellow)",
      fg: "#B45309",
      tid: "solution-children",
    },
    {
      title: "For administrators",
      promise: "Clarity, end-to-end.",
      href: "#admin",
      Icon: ShieldCheck,
      bg: "var(--pastel-sky)",
      fg: "#2563EB",
      tid: "solution-admin",
    },
  ];
  return (
    <section
      id="solution"
      data-testid="solution"
      className="py-24 sm:py-32 border-t border-[#EEF0F4]"
    >
      <div className="mx-auto max-w-7xl px-6 sm:px-10">
        <div className="max-w-3xl">
          <span className="eyebrow">One calm system</span>
          <h2 className="mt-4 font-display text-4xl sm:text-5xl tracking-tight leading-[1.05] text-[#1A1B2E]">
            One calm system for everyone
            <br />
            <span className="italic font-light text-[#1F3A2A]">in the day.</span>
          </h2>
          <p className="mt-6 text-lg text-[#6B7280] leading-relaxed">
            Alouvya connects the four people who make childcare work — parents,
            staff, children, and administrators — on a single platform designed
            for the way real centers operate.
          </p>
        </div>
        <div className="mt-12 grid sm:grid-cols-2 gap-5">
          {tiles.map((t) => (
            <a
              key={t.tid}
              href={t.href}
              data-testid={t.tid}
              className="group rounded-3xl bg-white border border-[#EEF0F4] p-8 flex items-start gap-5 transition-all duration-200 hover:-translate-y-0.5 hover:shadow-[0_8px_24px_rgba(16,24,40,0.08)]"
            >
              <span
                aria-hidden
                className="h-14 w-14 rounded-2xl flex items-center justify-center shrink-0"
                style={{ background: t.bg }}
              >
                <t.Icon size={26} strokeWidth={1.5} style={{ color: t.fg }} />
              </span>
              <div className="flex-1 min-w-0">
                <div className="eyebrow">{t.title}</div>
                <div className="mt-2 font-display text-xl text-[#1A1B2E] tracking-tight leading-snug">
                  {t.promise}
                </div>
                <div className="mt-3 inline-flex items-center gap-1 text-sm font-semibold text-[#1F3A2A]">
                  Read more
                  <ArrowRight
                    size={14}
                    strokeWidth={2}
                    className="transition-transform duration-200 group-hover:translate-x-0.5"
                  />
                </div>
              </div>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------------- 4.4 Parents ---------------- */
function ParentsSection() {
  return (
    <section
      id="parents"
      data-testid="section-parents"
      className="py-24 sm:py-32 bg-[#F7F8FC] border-t border-[#EEF0F4]"
    >
      <div className="mx-auto max-w-7xl px-6 sm:px-10 grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-6 order-2 lg:order-1">
          <span className="eyebrow">For parents</span>
          <h2 className="mt-4 font-display text-4xl sm:text-5xl tracking-tight leading-[1.05] text-[#1A1B2E]">
            Peace of mind,
            <br />
            <span className="italic font-light text-[#1F3A2A]">every day.</span>
          </h2>
          <p className="mt-3 text-lg text-[#6B7280]">
            A parent app designed for calm — not constant alerts.
          </p>
          <p className="mt-5 text-[#1A1B2E] leading-relaxed">
            See your child's current zone — Yard, Playroom, Lunchroom, Nap Room —
            updated by the teacher who's with them. Message your center without
            chasing a phone number. Tap one button when you arrive for pickup.
            That's the whole experience: quiet, complete, and respectful of your
            time.
          </p>
          <ul className="mt-7 space-y-3">
            {[
              "Real-time zone status, no live tracking",
              "One-tap pickup with staff confirmation",
              "Direct messages to your child's teacher",
              "Calendar of events, field trips, and closures",
              "Secure photos and moments — shared only with you",
              "Documents and forms in one place",
            ].map((f) => (
              <li
                key={f}
                className="flex items-start gap-3 text-[#1A1B2E] text-sm"
              >
                <Check
                  size={16}
                  strokeWidth={2}
                  className="mt-0.5 shrink-0 text-[#1F3A2A]"
                />
                {f}
              </li>
            ))}
          </ul>
          <figure className="mt-10 border-l-2 border-[#1F3A2A] pl-5">
            <blockquote className="italic text-[#1A1B2E] leading-relaxed">
              "I used to refresh a group chat all day. Now I just glance at the
              home screen and I know."
            </blockquote>
            <figcaption className="mt-2 text-xs text-[#6B7280]">
              — Parent quote placeholder
            </figcaption>
          </figure>
        </div>

        {/* Parent App mock */}
        <div className="lg:col-span-6 order-1 lg:order-2">
          <PhoneMock testid="parents-mock" />
        </div>
      </div>
    </section>
  );
}

/* ---------------- 4.5 Staff ---------------- */
function StaffSection() {
  return (
    <section
      id="staff"
      data-testid="section-staff"
      className="py-24 sm:py-32 border-t border-[#EEF0F4]"
    >
      <div className="mx-auto max-w-7xl px-6 sm:px-10 grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-7 order-2 lg:order-2">
          <span className="eyebrow">For staff</span>
          <h2 className="mt-4 font-display text-4xl sm:text-5xl tracking-tight leading-[1.05] text-[#1A1B2E]">
            Less paperwork.
            <br />
            <span className="italic font-light text-[#1F3A2A]">More care.</span>
          </h2>
          <p className="mt-3 text-lg text-[#6B7280]">
            Run the day in taps, not in checklists.
          </p>
          <p className="mt-5 text-[#1A1B2E] leading-relaxed">
            Every common action — move a group to the yard, start lunch, mark
            nap time, update a child's status — is one tap. The Live Overview
            shows your room at a glance, so you can stay present with the
            children instead of buried in a form.
          </p>
          <ul className="mt-7 space-y-3">
            {[
              "Live Overview of every child in your room",
              "Quick Actions for group-level transitions",
              "One-tap status updates per child",
              "Alerts surface what needs attention — nothing more",
              "Direct messaging to parents, in context",
              "Attendance check-in from any device",
            ].map((f) => (
              <li
                key={f}
                className="flex items-start gap-3 text-[#1A1B2E] text-sm"
              >
                <Check
                  size={16}
                  strokeWidth={2}
                  className="mt-0.5 shrink-0 text-[#1F3A2A]"
                />
                {f}
              </li>
            ))}
          </ul>
        </div>
        <div className="lg:col-span-5 order-1 lg:order-1">
          <TabletMock testid="staff-mock" />
        </div>
      </div>
    </section>
  );
}

/* ---------------- 4.6 Children ---------------- */
function ChildrenSection() {
  const tiles = [
    { label: "Yard", bg: "var(--pastel-mint)", Icon: TreePalm, fg: "#1F8A3F" },
    { label: "Play", bg: "var(--pastel-lavender)", Icon: Sparkles, fg: "#1F3A2A" },
    { label: "Eat", bg: "var(--pastel-yellow)", Icon: Coffee, fg: "#B45309" },
    { label: "Rest", bg: "var(--pastel-sky)", Icon: Moon, fg: "#2563EB" },
    { label: "In care", bg: "var(--pastel-pink)", Icon: HandHeart, fg: "#BE185D" },
  ];
  const badges = [
    {
      t: "Easy to use",
      b: "Big icons. No text needed.",
    },
    {
      t: "Empowering",
      b: "Kids can update their own status.",
    },
    {
      t: "Safe & fun",
      b: "Colorful, friendly, and locked down by design.",
    },
  ];
  return (
    <section
      id="children"
      data-testid="section-children"
      className="py-24 sm:py-32 bg-[#F7F8FC] border-t border-[#EEF0F4]"
    >
      <div className="mx-auto max-w-5xl px-6 sm:px-10 text-center">
        <span className="eyebrow">For children</span>
        <h2 className="mt-4 font-display text-4xl sm:text-5xl tracking-tight leading-[1.05] text-[#1A1B2E]">
          Made for{" "}
          <span className="italic font-light text-[#1F3A2A]">small hands.</span>
        </h2>
        <p className="mt-3 text-lg text-[#6B7280]">
          The only childcare platform where children themselves can update
          their status.
        </p>
        <p className="mt-5 text-[#1A1B2E] leading-relaxed max-w-2xl mx-auto">
          On a wall-mounted tablet, children tap a big friendly tile — Yard,
          Play, Eat, Rest, In Care — to share what they're doing. It builds
          participation, agency, and a sense of ownership over their day.
          Staff confirm in the background. Parents see the update on their
          phone.
        </p>

        <div className="mt-12 rounded-3xl bg-white border border-[#EEF0F4] p-7 sm:p-10 max-w-3xl mx-auto shadow-[0_8px_24px_rgba(16,24,40,0.06)]">
          <div className="font-display text-2xl text-[#1A1B2E] tracking-tight">
            Hi Amira! Where are you now?
          </div>
          <div className="mt-7 grid grid-cols-3 sm:grid-cols-5 gap-3">
            {tiles.map((t) => (
              <div
                key={t.label}
                data-testid={`children-tile-${t.label.toLowerCase().replace(" ", "-")}`}
                className="rounded-2xl aspect-square flex flex-col items-center justify-center gap-2 transition-transform duration-200 hover:scale-[1.02] shadow-[0_4px_18px_rgba(16,24,40,0.06)]"
                style={{ background: t.bg }}
              >
                <t.Icon size={36} strokeWidth={1.5} style={{ color: t.fg }} />
                <div
                  className="font-display text-sm font-semibold"
                  style={{ color: t.fg }}
                >
                  {t.label}
                </div>
              </div>
            ))}
          </div>
        </div>

        <ul className="mt-10 grid sm:grid-cols-3 gap-4 max-w-3xl mx-auto">
          {badges.map((b) => (
            <li
              key={b.t}
              className="rounded-2xl bg-white border border-[#EEF0F4] p-5"
            >
              <div className="font-semibold text-[#1A1B2E]">{b.t}</div>
              <div className="text-sm text-[#6B7280] mt-1.5">{b.b}</div>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

/* ---------------- 4.7 Admin ---------------- */
function AdminSection() {
  return (
    <section
      id="admin"
      data-testid="section-admin"
      className="py-24 sm:py-32 border-t border-[#EEF0F4]"
    >
      <div className="mx-auto max-w-7xl px-6 sm:px-10 grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-5">
          <span className="eyebrow">For administrators</span>
          <h2 className="mt-4 font-display text-4xl sm:text-5xl tracking-tight leading-[1.05] text-[#1A1B2E]">
            Clarity,
            <br />
            <span className="italic font-light text-[#1F3A2A]">end-to-end.</span>
          </h2>
          <p className="mt-3 text-lg text-[#6B7280]">
            Run a center, a region, or a network — with the same instruments.
          </p>
          <p className="mt-5 text-[#1A1B2E] leading-relaxed">
            KPIs, attendance, status distribution, communication volume,
            upcoming events, recent activity, billing — everything an
            administrator needs to see, in one quiet dashboard. Reports
            generate themselves. Audit logs capture every sensitive action
            automatically.
          </p>
          <ul className="mt-7 space-y-3">
            {[
              "Multi-center overview from a single account",
              "Attendance, status, and communication analytics",
              "Built-in reporting — no spreadsheet rebuilds",
              "Audit log of every sensitive action",
              "Granular roles and permissions",
              "Bulk announcements with audience controls",
            ].map((f) => (
              <li
                key={f}
                className="flex items-start gap-3 text-[#1A1B2E] text-sm"
              >
                <Check
                  size={16}
                  strokeWidth={2}
                  className="mt-0.5 shrink-0 text-[#1F3A2A]"
                />
                {f}
              </li>
            ))}
          </ul>
        </div>
        <div className="lg:col-span-7">
          <AdminMock testid="admin-mock" />
        </div>
      </div>
    </section>
  );
}

/* ---------------- 4.8 Privacy ---------------- */
function PrivacySection() {
  return (
    <section
      id="privacy-promise"
      data-testid="section-privacy"
      className="py-24 sm:py-32 bg-[#132619] text-white"
    >
      <div className="mx-auto max-w-5xl px-6 sm:px-10 text-center">
        <span className="text-xs tracking-[0.04em] uppercase font-semibold text-[#3D5C46]">
          Privacy-first promise
        </span>
        <h2 className="mt-4 font-display text-4xl sm:text-5xl tracking-tight leading-[1.05]">
          Show,{" "}
          <span className="italic font-light text-[#3D5C46]">don't surveil.</span>
        </h2>
        <p className="mt-3 text-lg text-white/70">
          Privacy isn't a feature we added. It's the design.
        </p>
        <p className="mt-6 max-w-2xl mx-auto text-white/85 leading-relaxed">
          Children deserve a childhood that isn't recorded minute-by-minute.
          Parents deserve to know what they need to know. Staff deserve to do
          their work without being watched. Alouvya was built to honor all three.
        </p>
      </div>

      <div className="mt-14 mx-auto max-w-6xl px-6 sm:px-10 grid md:grid-cols-3 gap-5">
        {[
          {
            t: "Zone, not GPS.",
            b: "We show that a child is in the Yard or the Playroom. We never show a moving dot on a map. There is no live video. There is no continuous location feed.",
            tid: "privacy-zone",
          },
          {
            t: "Consent, by default.",
            b: "Photos are visible only to the guardians of the children tagged. Documents are scoped to the people who need them. Parents control who can see what.",
            tid: "privacy-consent",
          },
          {
            t: "Audited, end-to-end.",
            b: "Every sensitive action — every check-in, message, status change, pickup, permission grant — is logged. Administrators can prove what happened. Families can request what's stored.",
            tid: "privacy-audited",
          },
        ].map((p) => (
          <article
            key={p.tid}
            data-testid={p.tid}
            className="rounded-3xl bg-white/5 border border-white/10 p-7 backdrop-blur-sm"
          >
            <h3 className="font-display text-2xl tracking-tight">{p.t}</h3>
            <p className="mt-3 text-white/75 leading-relaxed text-sm">{p.b}</p>
          </article>
        ))}
      </div>

      <p className="mt-12 text-center text-xs text-white/55 max-w-2xl mx-auto px-6">
        Aligned with COPPA, GDPR, FERPA, and PIPEDA. Data encrypted at rest and
        in transit. Per-organization data isolation.
      </p>
    </section>
  );
}

/* ---------------- 4.9 Smart Building ---------------- */
function SmartBuildingSection() {
  return (
    <section
      id="smart-building"
      data-testid="section-smart-building"
      className="py-24 sm:py-32 border-t border-[#EEF0F4] bg-[#F7F8FC]"
    >
      <div className="mx-auto max-w-7xl px-6 sm:px-10 grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-6">
          <span className="eyebrow">Smart building ready</span>
          <h2 className="mt-4 font-display text-4xl sm:text-5xl tracking-tight leading-[1.05] text-[#1A1B2E]">
            Ready for{" "}
            <span className="italic font-light text-[#1F3A2A]">what comes next.</span>
          </h2>
          <p className="mt-3 text-lg text-[#6B7280]">
            An open platform designed to grow into your building.
          </p>
          <p className="mt-5 text-[#1A1B2E] leading-relaxed">
            Alouvya is built on a zone-aware data model and an open API — so it
            can connect to access control systems, door sensors, IoT devices,
            and facility platforms as your center adds them. Add a smart door
            tomorrow; Alouvya reflects it the same day.
          </p>
          <ul className="mt-7 space-y-3">
            {[
              "Open REST and webhook API",
              "Zone-aware status model — extendable to any room or area",
              "Device registry for kiosks, tablets, and sensors",
              "Single sign-on (SAML, OIDC) for multi-center networks",
              "Webhook events for attendance, status, pickup, and messaging",
              "Access control integrations and door-sensor sync — coming soon",
            ].map((f) => (
              <li
                key={f}
                className="flex items-start gap-3 text-[#1A1B2E] text-sm"
              >
                <Check
                  size={16}
                  strokeWidth={2}
                  className="mt-0.5 shrink-0 text-[#1F3A2A]"
                />
                {f}
              </li>
            ))}
          </ul>
        </div>

        {/* Diagram-ish illustration */}
        <div className="lg:col-span-6">
          <div className="relative rounded-3xl bg-white border border-[#EEF0F4] p-7 shadow-[0_8px_24px_rgba(16,24,40,0.06)] aspect-[4/3] flex items-center justify-center">
            <div className="grid grid-cols-3 gap-4 w-full">
              {[
                { L: "Parents", c: "#1F3A2A", bg: "#E6EFE8", Icon: HandHeart },
                { L: "Staff", c: "#1F8A3F", bg: "#DFF5E1", Icon: Hand },
                { L: "Admin", c: "#2563EB", bg: "#DCE9FF", Icon: ShieldCheck },
                { L: "Kiosk", c: "#B45309", bg: "#FFF4D6", Icon: Sparkles },
                {
                  L: "Open API",
                  c: "#1A1B2E",
                  bg: "#F7F8FC",
                  Icon: MessageCircle,
                  big: true,
                },
                { L: "Sensors", c: "#BE185D", bg: "#FCE4EC", Icon: CalendarCheck },
              ].map((n) => (
                <div
                  key={n.L}
                  className={
                    "rounded-2xl border border-[#EEF0F4] p-4 text-center " +
                    (n.big ? "ring-2 ring-[#1F3A2A]/30 shadow-[0_4px_18px_rgba(107,91,231,0.15)]" : "")
                  }
                  style={{ background: n.bg }}
                >
                  <div className="mx-auto h-8 w-8 rounded-full flex items-center justify-center" style={{ color: n.c }}>
                    <n.Icon size={18} strokeWidth={1.6} />
                  </div>
                  <div
                    className="mt-2 text-xs font-semibold"
                    style={{ color: n.c }}
                  >
                    {n.L}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ---------------- 4.10 Pricing teaser ---------------- */
function PricingTeaser() {
  return (
    <section
      id="pricing-teaser"
      data-testid="section-pricing-teaser"
      className="py-24 sm:py-32 border-t border-[#EEF0F4]"
    >
      <div className="mx-auto max-w-5xl px-6 sm:px-10 text-center">
        <span className="eyebrow">Pricing</span>
        <h2 className="mt-4 font-display text-4xl sm:text-5xl tracking-tight leading-[1.05] text-[#1A1B2E]">
          Pricing that grows{" "}
          <span className="italic font-light text-[#1F3A2A]">with your center.</span>
        </h2>
        <p className="mt-6 max-w-2xl mx-auto text-lg text-[#6B7280] leading-relaxed">
          Simple plans for single centers, networks, and enterprise operators.
          Talk to us about volume and multi-center pricing.
        </p>
        <Link
          to="/pricing"
          data-testid="pricing-teaser-cta"
          className="mt-10 btn-primary inline-flex"
        >
          See plans
          <ArrowRight size={16} strokeWidth={2} />
        </Link>
      </div>
    </section>
  );
}

/* ---------------- 4.11 Demo CTA band ---------------- */
function DemoBand() {
  const { t } = useI18n();
  return (
    <section
      data-testid="section-demo-band"
      className="py-24 sm:py-32 bg-[#1F3A2A] text-white"
    >
      <div className="mx-auto max-w-4xl px-6 sm:px-10 text-center">
        <h2 className="font-display text-4xl sm:text-6xl tracking-tight leading-[1.05]">
          {t("landing.demo_band.headline_lead")}{" "}
          <span className="italic font-light text-[#E6EFE8]">
            {t("landing.demo_band.headline_italic")}
          </span>{" "}
          {t("landing.demo_band.headline_tail")}
        </h2>
        <p className="mt-6 text-lg text-white/85 max-w-xl mx-auto leading-relaxed">
          {t("landing.demo_band.subhead")}
        </p>
        <Link
          to="/demo"
          data-testid="demo-band-cta"
          className="mt-10 inline-flex items-center justify-center px-7 py-3.5 rounded-full bg-white text-[#1A1B2E] text-base font-semibold hover:bg-[#E6EFE8] transition-colors"
        >
          {t("landing.cta.book_demo")}
          <ArrowRight size={18} strokeWidth={2} className="ml-2" />
        </Link>
      </div>
    </section>
  );
}

/* ============================================================
   Inline visual mocks — simplified per audience surface.
   ============================================================ */

function PhoneMock({ testid }) {
  return (
    <div
      data-testid={testid}
      className="relative mx-auto max-w-xs rounded-[40px] bg-[#132619] p-2 shadow-[0_16px_40px_rgba(16,24,40,0.18)]"
    >
      <div className="rounded-[34px] bg-white overflow-hidden">
        <div className="px-5 pt-5 pb-3 flex items-center justify-between">
          <span className="text-[10px] uppercase tracking-[0.04em] font-semibold text-[#6B7280]">
            Home
          </span>
          <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.04em] font-semibold text-[#1F8A3F]">
            <span aria-hidden className="h-1.5 w-1.5 rounded-full bg-[#34C759]" />
            Up to date
          </span>
        </div>
        <div className="px-5">
          <div className="flex items-center gap-3">
            <div className="h-12 w-12 rounded-full bg-[#DFF5E1] flex items-center justify-center font-display text-xl text-[#1F8A3F] ring-2 ring-[#34C759]/40">
              L
            </div>
            <div>
              <div className="font-display text-lg text-[#1A1B2E] leading-tight">
                Leo
              </div>
              <div className="text-xs text-[#6B7280]">Sunshine Room</div>
            </div>
          </div>
          <div className="mt-4 rounded-2xl bg-[#DFF5E1] px-4 py-5 relative overflow-hidden">
            <div className="text-[10px] uppercase tracking-[0.04em] font-semibold text-[#1A1B2E]/55">
              Right now
            </div>
            <div className="mt-2 font-display text-2xl text-[#1A1B2E] leading-tight">
              In the yard
            </div>
            <p className="mt-1 text-xs text-[#1A1B2E]/70">
              Updated 2 min ago by Sarah Chen.
            </p>
          </div>
        </div>
        <div className="mt-4 mx-5 mb-5 rounded-2xl bg-[#F7F8FC] p-4">
          <div className="text-[10px] uppercase tracking-[0.04em] font-semibold text-[#6B7280]">
            Today's plan
          </div>
          <ul className="mt-2 space-y-2 text-xs text-[#1A1B2E]">
            <li className="flex justify-between">
              <span>Morning circle</span>
              <span className="text-[#6B7280]">9:00</span>
            </li>
            <li className="flex justify-between">
              <span>Outdoor play</span>
              <span className="text-[#6B7280]">10:00</span>
            </li>
            <li className="flex justify-between">
              <span>Lunch</span>
              <span className="text-[#6B7280]">12:00</span>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
}

function TabletMock({ testid }) {
  const cards = [
    { name: "Leo", state: "In yard", c: "#1F8A3F", bg: "#DFF5E1" },
    { name: "Amira", state: "Eating", c: "#B45309", bg: "#FFF4D6" },
    { name: "Noor", state: "Resting", c: "#2563EB", bg: "#DCE9FF" },
    { name: "Sam", state: "Playing", c: "#1F3A2A", bg: "#E6EFE8" },
  ];
  return (
    <div
      data-testid={testid}
      className="rounded-3xl bg-white border border-[#EEF0F4] shadow-[0_8px_24px_rgba(16,24,40,0.08)] p-5"
    >
      <div className="flex items-center justify-between">
        <div>
          <div className="font-display text-lg text-[#1A1B2E] leading-tight">
            Sunshine Room
          </div>
          <div className="text-[11px] text-[#6B7280]">12 children · 2 staff</div>
        </div>
        <span className="trust-pill">All present</span>
      </div>
      <div className="mt-5 grid grid-cols-2 gap-3">
        {cards.map((c) => (
          <div
            key={c.name}
            className="rounded-2xl border border-[#EEF0F4] p-3 flex items-center gap-3"
          >
            <div
              className="h-9 w-9 rounded-full flex items-center justify-center font-display text-sm"
              style={{ background: c.bg, color: c.c }}
            >
              {c.name[0]}
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-semibold text-[#1A1B2E]">{c.name}</div>
              <div className="text-[10px] uppercase tracking-[0.04em] font-semibold" style={{ color: c.c }}>
                {c.state}
              </div>
            </div>
          </div>
        ))}
      </div>
      <div className="mt-5 rounded-2xl bg-[#132619] text-white p-3 flex gap-2 overflow-x-auto">
        {["Move to yard", "Start lunch", "Nap time", "In care"].map((q) => (
          <span
            key={q}
            className="text-[11px] font-semibold px-3 py-1.5 rounded-full bg-white/8 shrink-0"
          >
            {q}
          </span>
        ))}
      </div>
    </div>
  );
}

function AdminMock({ testid }) {
  return (
    <div
      data-testid={testid}
      className="rounded-3xl bg-white border border-[#EEF0F4] p-6 shadow-[0_8px_24px_rgba(16,24,40,0.06)]"
    >
      <div className="flex items-center justify-between">
        <div>
          <span className="eyebrow">Overview</span>
          <div className="font-display text-xl text-[#1A1B2E] mt-1">
            Bright Beginnings Childcare
          </div>
        </div>
        <span className="text-xs text-[#6B7280]">Sunshine Center</span>
      </div>
      <div className="mt-5 grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { L: "Total Children", v: "60", bg: "#E6EFE8", c: "#1F3A2A" },
          { L: "Checked In", v: "47", bg: "#DFF5E1", c: "#1F8A3F" },
          { L: "Staff On Duty", v: "8", bg: "#FFF4D6", c: "#B45309" },
          { L: "Messages", v: "23", bg: "#FCE4EC", c: "#BE185D" },
        ].map((k) => (
          <div
            key={k.L}
            className="rounded-2xl p-4"
            style={{ background: k.bg }}
          >
            <div className="text-[10px] uppercase tracking-[0.04em] font-semibold" style={{ color: k.c }}>
              {k.L}
            </div>
            <div className="font-display text-2xl tabular-nums mt-1" style={{ color: k.c }}>
              {k.v}
            </div>
          </div>
        ))}
      </div>
      <div className="mt-5 grid grid-cols-3 gap-3 text-xs">
        {["Yard", "Playroom", "Eating", "Nap room", "In care"].map((z) => (
          <div
            key={z}
            className="rounded-xl bg-[#F7F8FC] px-3 py-2.5 text-center text-[#1A1B2E] font-medium"
          >
            {z}
          </div>
        ))}
      </div>
    </div>
  );
}
